define('viewModels/SwitchedView',[
    'lib/knockout',
    'lib/lodash4',
    'viewModels/ViewModel',
    'services/loggingService'
], function (ko, _, ViewModel, loggingService) {
    'use strict';

    /**
     * ViewModel used to switch between mutually exclusive views.
     * When switching views, the old active view is destroyed and
     * a new instance of the type being switched to is created.
     *
     * @exports viewModels/SwitchedView
     */
    var SwitchedView = ViewModel.extend(function (viewModels) {
        var self = this;
        var activeView = ko.observable(); // The name of the view that is currently active.
        var viewData = {};

        /**
         * Returns the name of the currently active view.
         */
        self.getActiveView = self.computed(function () {
            return activeView();
        });

        /**
         * Makes a view active, returning an instance of it. This returns an instance of the view.
         */
        self.setActiveView = function (viewName) {
            var args = Array.prototype.slice.call(arguments, 1);

            if (self[viewName]) {
                if (viewName === activeView()) {
                    self.disposeView(viewName);
                }

                activateView(viewName, args);
                return self[viewName]();
            }
            else if (viewName === undefined || viewName === null) {
                activateView(viewName, []);
            }
            else {
                var message = 'No sub-view with name "' + viewName + '" was found.';
                loggingService.error(message);
                throw message;
            }
        };

        /**
         * Adds a view that always returns a NEW instance when it becomes active. Chainable.
         */
        self.addView = function (viewName, Constructor) {
            var args = Array.prototype.slice.call(arguments, 2);
            viewData[viewName] = {
                Constructor: Constructor,
                instance: null,
                persist: false,
                arguments: args
            };
            self[viewName] = ko.observable();
            return self;
        };

        /**
         * Adds a view that always returns the SAME instance when it becomes active.  Chainable.
         */
        self.addPersistentView = function (viewName) {
            self.addView.apply(this, arguments);
            viewData[viewName].persist = true;
            return self;
        };

        /**
         * Explicitly invalidates the singleton instance of a persisted view.
         */
        self.invalidateViewInstance = function (viewName) {
            if (viewData[viewName].instance) {
                viewData[viewName].instance.dispose();
                viewData[viewName].instance = null;
            }
        };

        self.onDispose = function () {
            if (activeView()) {
                self.disposeView(activeView());
                activeView(undefined);
            }
            disposeInactiveViews(true);
        };

        self.disposeView = function (viewName) {
            var view = self[viewName]();
            if (isDisposable(view)) {
                self.disposeSubView(view);
            }
            self[viewName](undefined);
        };

        var notSelected = function (viewName) {
            return (activeView() !== viewName);
        };

        var isDisposable = function (view) {
            return (view && _.isFunction(view.dispose));
        };

        var isNotPersisted = function (view) {
            if (view) {
                return _.find(viewData, function (meta) {
                    return meta.instance === view && !meta.persist;
                });
            }
        };

        var disposeInactiveViews = function (disposePersistedFlag) {
            _(viewData).keys().filter(notSelected)
                .each(function (viewName) {
                    var view = self[viewName]();
                    if (isDisposable(view) && (disposePersistedFlag || isNotPersisted(view))) {
                        self.disposeSubView(view);
                    }
                    self[viewName](undefined);
                });
        };

        // Activates a view by its name.
        var activateView = function (viewName, args) {
            if (viewName) {
                var meta = viewData[viewName];
                if (meta.Constructor) {
                    args = args.length > 0 ? args : meta.arguments;
                    if (meta.persist) {
                        activatePersistentView(viewName, meta.Constructor, args);
                    }
                    else {
                        activateTransientView(viewName, meta.Constructor, args);
                    }
                }
            }
            activeView(viewName);
            disposeInactiveViews(false);
        };

        // Activates a view by retrieving a singleton instance.
        var activatePersistentView = function (viewName, Constructor, args) {
            var viewInstance = viewData[viewName].instance;
            if (viewInstance) {
                self[viewName](viewInstance);
            }
            else {
                // If there is no instance then make one.
                activateTransientView(viewName, Constructor, args);
            }
        };

        // Activates a view by creating a new instance.
        var activateTransientView = function (viewName, Constructor, args) {
            args.unshift(null);
            var ViewFactory = Constructor.bind.apply(Constructor, args);
            var viewInstance = self.subView(ViewFactory);
            viewData[viewName].instance = viewInstance;
            self[viewName](viewInstance);
        };

        /**
         * This is primarily left for backwards compatibility.
         */
        _.each(viewModels, function (Constructor, viewName) {
            self.addView(viewName, Constructor);
        });
    });

    return SwitchedView;
});

